import {RailsPartial} from '@github-ui/rails-partial'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {railsPartialQuery} from '@github-ui/react-core/future/rails-partial-query'
import {RootAppRouteErrorInner} from '@github-ui/react-core/future/RouterErrorBoundary'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {type RouteObject, useRouteError} from '@github-ui/react-router'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useEffect, useRef} from 'react'

import type {ActionsShellRouteHandle} from './workflows-layout'

const fragmentRoutes = [
  {
    id: 'actionsCachesRoute',
    path: '/:owner/:repo/actions/caches',
    title: 'Caches',
    selection: 'caches',
  },
  {
    id: 'actionsRunnersRoute',
    path: '/:owner/:repo/actions/runners',
    title: 'Runners',
    selection: 'runners',
  },
] as const

export function actionsRailsFragmentQueryDeps({
  pathname,
  searchParams,
}: {
  pathname: string
  searchParams: URLSearchParams
}) {
  const search = searchParams.toString()
  return {pathname: search ? `${pathname}?${search}` : pathname}
}

export function isDisabledActionsFragmentError(error: unknown) {
  return isResponseError(error) && error.response.status === 404
}

export function ActionsRailsFragmentErrorBoundary({appName}: {appName: string}) {
  const error = useRouteError()
  const fragmentDisabled = isDisabledActionsFragmentError(error)
  const recoveryStarted = useRef(false)

  useEffect(() => {
    if (fragmentDisabled && !recoveryStarted.current && ssrSafeWindow) {
      recoveryStarted.current = true
      ssrSafeWindow.location.assign(ssrSafeWindow.location.href)
    }
  }, [fragmentDisabled])

  return fragmentDisabled ? null : <RootAppRouteErrorInner routeError={error} appName={appName} />
}

export function createActionsRailsFragmentRoutes(appBuilder: DataRouterApplicationBuilder<string>): RouteObject[] {
  const FragmentErrorBoundary = () => <ActionsRailsFragmentErrorBoundary appName={appBuilder.name} />

  return fragmentRoutes.map(({id, path, title, selection}) => {
    const route = appBuilder.createQueryRouteConfig(id, {
      path,
      queries: [
        railsPartialQuery({
          partialName: 'Main',
          queryDeps: actionsRailsFragmentQueryDeps,
        }),
      ],
    })

    const shell = {
      kind: 'rails-fragment',
      title,
      getSelection: () => ({type: selection}),
    } satisfies ActionsShellRouteHandle

    return route.toRoute({
      element: <RailsPartial partialName="Main" route={route} />,
      errorBoundary: {override: FragmentErrorBoundary},
      handle: {actionsShell: shell},
    })
  })
}
